import { ClientSoftwareResponse } from '@kcftech/prometheus-client';
import { DataSourceTable } from 'components/data_source_table/DataSourceTable';
import { TableColumn } from 'components/data_table/types';
import { NoValue } from 'components/no_value/NoValue';
import { ClientSoftwareDataSource } from 'custom_hooks/data_hooks/data_source/clientSoftwareDataSource';
import { clientSoftwareCopy } from 'pages/Settings/ClientSoftwareTags/clientSoftwareCopy';
import { ClientSoftwareDescriptionCell } from 'pages/Settings/ClientSoftwareTags/table_cells/ClientSoftwareDescriptionCell';
import { ClientSoftwareTagsCell } from 'pages/Settings/ClientSoftwareTags/table_cells/ClientSoftwareTagsCell';
import { useMemo } from 'react';
import { InfoPopup } from 'shared/info_popup/InfoPopup';
import styled from 'styled-components';

const HeaderLabel = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
`;

export type ClientSoftwareTagsTableProps = Readonly<{
    dataSource: ClientSoftwareDataSource;
}>;

const getRowKey = (row: ClientSoftwareResponse) => String(row.Id);

export function ClientSoftwareTagsTable({ dataSource }: ClientSoftwareTagsTableProps) {
    const { reloadPage } = dataSource;

    const columns = useMemo<TableColumn<ClientSoftwareResponse>[]>(
        () => [
            {
                header: () => (
                    <HeaderLabel>
                        Name
                        <InfoPopup title="Name" type="info">
                            {clientSoftwareCopy.columns.name}
                        </InfoPopup>
                    </HeaderLabel>
                ),
                id: 'Name',
                value: ({ rowData }) => <code>{rowData.Name ?? rowData.ApiClient}</code>,
                sortOn: 'Name',
                primary: true,
                locked: true,
            },
            {
                header: () => (
                    <HeaderLabel>
                        API Client
                        <InfoPopup title="API Client" type="info">
                            {clientSoftwareCopy.columns.apiClient}
                        </InfoPopup>
                    </HeaderLabel>
                ),
                id: 'ApiClient',
                value: ({ rowData }) => <code>{rowData.ApiClient}</code>,
                sortOn: 'ApiClient',
            },
            {
                header: () => (
                    <HeaderLabel>
                        Known Client
                        <InfoPopup title="Known Client" type="info">
                            {clientSoftwareCopy.columns.knownClient}
                        </InfoPopup>
                    </HeaderLabel>
                ),
                id: 'KnownApiClient',
                value: ({ rowData }) => (
                    <code>{rowData.KnownApiClient ?? <NoValue />}</code>
                ),
                sortOn: 'KnownApiClient',
            },
            {
                header: () => (
                    <HeaderLabel>
                        Description
                        <InfoPopup title="Description" type="info">
                            {clientSoftwareCopy.columns.description}
                        </InfoPopup>
                    </HeaderLabel>
                ),
                id: 'Description',
                value: ({ rowData }) => (
                    <ClientSoftwareDescriptionCell
                        rowData={rowData}
                        onChange={reloadPage}
                    />
                ),
            },
            {
                header: () => (
                    <HeaderLabel>
                        Tags
                        <InfoPopup title="Tags" type="info">
                            {clientSoftwareCopy.columns.tags}
                        </InfoPopup>
                    </HeaderLabel>
                ),
                id: 'Tags',
                value: ({ rowData }) => (
                    <ClientSoftwareTagsCell clientSoftwareId={rowData.Id} />
                ),
            },
        ],
        [reloadPage],
    );

    return (
        <DataSourceTable
            dataSource={dataSource}
            rowKey={getRowKey}
            columns={columns}
            noResultMessage="No client softwares found."
        />
    );
}
